import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getErrorStatus, getErrorMessageKey } from '@/shared/constants/errorCodes';
import { selectIsIdolLocked } from '@/shared/store/slices/idolSlice';

/**
 * 同一時間只讓一個 StatusModal 開著。
 *
 * 一個畫面上常有多個各自獨立、各自 fetch 的區塊（首頁的 HomeLayout／IdolsRecommend／
 * WorldViewPreview／AppNavbar 就有四個），後端同時回錯時它們會各開一個 modal，
 * 疊出多層 backdrop；而 bootstrap 的 body 捲動鎖是後進先出，疊起來再逐一關掉容易
 * 留下關不掉的灰幕或整頁不能捲。同時發生的錯誤幾乎都是同一個原因（500、429、
 * 帳號停權），顯示第一個就夠，其餘記 log 讓問題仍看得見。
 */
let openModalToken = null;

/**
 * onClose：使用者關閉 modal 後呼叫（例如內容無法查看時導回首頁）。
 * ref.show() 回傳是否真的顯示：被全站鎖略過時回傳 false，onClose 也不會被呼叫，
 * 呼叫端若依賴 onClose 導頁，要在拿到 false 時自行處理，否則畫面會停在原地。
 */
const StatusModal = forwardRef(({ statusCode, size = "sm", onClose, children }, ref) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const tokenRef = useRef({});

  const releaseLock = () => {
    if (openModalToken === tokenRef.current) {
      openModalToken = null;
    }
  };

  // 卸載時一定要放行，否則後續全站的 StatusModal 都會被這把鎖擋掉
  useEffect(() => releaseLock, []);

  const handleClose = () => {
    releaseLock();
    setShow(false);
  };
  // 使用者關閉（叉叉、點背景、Esc）才觸發 onClose；ref.hide() 與被全域接手時的關閉不觸發
  const handleUserClose = () => {
    handleClose();
    onClose?.();
  };
  const handleShow = () => {
    if (openModalToken && openModalToken !== tokenRef.current) {
      console.warn(`StatusModal: 已有其他狀態彈窗開啟中，略過 ${statusCode}`);
      return false;
    }
    openModalToken = tokenRef.current;
    setShow(true);
    return true;
  };

  useImperativeHandle(ref, () => ({
    show: handleShow,
    hide: handleClose,
  }));

  // 由全域接手的停權碼不在這裡顯示：USER_2034 由帳號停權通知處理，
  // IDOL_3044 在目前偶像已鎖定時由偶像停權鎖定 modal 處理。
  // 必須在 render 時判斷：呼叫端是 setCurrentStatusCode 後立刻 show()，show() 當下拿到的 statusCode 還是舊值。
  const isIdolLocked = useSelector(selectIsIdolLocked);
  const isHandledGlobally = statusCode === 'USER_2034' || (statusCode === 'IDOL_3044' && isIdolLocked);

  // 被全域接手時關閉並釋放鎖，否則全站其他 StatusModal 會被這個看不見的彈窗擋住
  // （內容同 handleClose；直接寫在 effect 內，因為 handleClose 每次 render 都是新函式）
  useEffect(() => {
    if (show && isHandledGlobally) {
      if (openModalToken === tokenRef.current) {
        openModalToken = null;
      }
      setShow(false);
    }
  }, [show, isHandledGlobally]);

  // 根據狀態碼獲取狀態和訊息
  const status = getErrorStatus(statusCode);
  const messageKey = getErrorMessageKey(statusCode);
  const message = t(messageKey);

  let iconToShow = null;

  switch (status) {
    case 'success':
      iconToShow = <FontAwesomeIcon icon="fa-solid fa-circle-check" className="icon mb-3" />;
      break;
    case 'error':
      iconToShow = <FontAwesomeIcon icon="fa-solid fa-circle-xmark" className="icon mb-3" />;
      break;
    case 'warning':
      iconToShow = <FontAwesomeIcon icon="fa-solid fa-circle-exclamation" className="icon mb-3" />;
      break;
    default:
      iconToShow = null;
      break;
  }

  return (
    <>
      <Modal size={size} show={show && !isHandledGlobally} onHide={handleUserClose} centered>
        <Modal.Header className="border-bottom-0" closeButton>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            {iconToShow}
            <div className='mb-0'>
              {children ? children : <p>{message}</p>}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
});
StatusModal.displayName = 'StatusModal';

export default StatusModal;